import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetVenues } from '@openapi/venues/venues';
import { useGetShows } from '@openapi/shows/shows';
import { useGetCommissionGroups } from '@openapi/commission-groups/commission-groups';
import { affiliateInvoiceDispatch } from '@openapi/reports/reports';
import useNotify from '../hooks/useNotify';
import { appendDateRangeParams } from '../helpers/dateParams';

const GenerateInvoiceModal = ({ onGenerated, showModal, MODAL_TYPE }) => {
    const { t } = useTranslation();
    const { notifyError, notifyInfo, notifySuccess } = useNotify();

    const [venueId, setVenueId] = useState(null);
    const [showId, setShowId] = useState(0);
    const [commissionGroupId, setCommissionGroupId] = useState(0);
    const [showDay, setShowDay] = useState('');
    const [showDateFrom, setShowDateFrom] = useState('');
    const [showDateTo, setShowDateTo] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
    const [duplicateConfirmWarnings, setDuplicateConfirmWarnings] = useState([]);

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const pickShowDay = (value) => {
        setShowDay(value);
        if (value) {
            setShowDateFrom('');
            setShowDateTo('');
        }
    };
    const pickShowDateFrom = (value) => {
        setShowDateFrom(value);
        if (value) setShowDay('');
    };
    const pickShowDateTo = (value) => {
        setShowDateTo(value);
        if (value) setShowDay('');
    };

    const { data: venueData } = useGetVenues({}, { query: { onError: notifyError } });
    const venues = venueData?.venues || [];

    const { data: showsData, isFetching: fetchingShows } = useGetShows(
        { venueId: venueId || undefined, pageSize: 1000 },
        { query: { enabled: !!venueId, onError: notifyError } },
    );
    const shows = showsData?.shows || [];

    const { data: commissionGroupsData } = useGetCommissionGroups(Number(showId), {
        query: { enabled: !!showId, onError: notifyError },
    });
    const commissionGroups = commissionGroupsData?.commissionGroups || [];

    // Always pre-fill the first venue (stakeholder preference).
    useEffect(() => {
        const availableVenues = venueData?.venues || [];
        if (!venueId && availableVenues.length > 0) {
            setVenueId(availableVenues[0].id);
        }
    }, [venueData?.venues, venueId]);

    // Default to the only show per venue, but let user resets stick (re-arms on venue change).
    // Wait for the shows fetch to settle so we don't act on a stale list from the previous venue.
    const defaultedShowForVenueRef = useRef(null);
    useEffect(() => {
        if (fetchingShows) return;
        if (defaultedShowForVenueRef.current !== venueId && showId === 0 && shows.length === 1) {
            defaultedShowForVenueRef.current = venueId;
            setShowId(shows[0].id);
            return;
        }
        if (showId === 0) return;
        if (!shows.some((show) => show.id === showId)) {
            setShowId(0);
            setCommissionGroupId(0);
        }
    }, [showId, shows, venueId, fetchingShows]);

    useEffect(() => {
        setCommissionGroupId(0);
    }, [showId]);

    const generateInvoice = async (confirmed = false) => {
        const params = { venueId, showId, commissionGroupId };
        const effectiveFrom = showDay || showDateFrom;
        const effectiveTo = showDay || showDateTo;
        appendDateRangeParams(params, [
            ['showDateFrom', effectiveFrom, 'T00:00:00'],
            ['showDateTo', effectiveTo, 'T23:59:59'],
        ]);
        params.confirmed = confirmed;
        return affiliateInvoiceDispatch(params);
    };

    const handleDispatchSuccess = async (result) => {
        notifySuccess(
            t('reports.affiliateInvoiceDispatchSuccess', {
                sent: result?.sent || 0,
                skipped: result?.skipped || 0,
                failed: result?.failed || 0,
            }),
        );
        if (onGenerated) await onGenerated();
        closeAction();
    };

    const handleGenerateInvoice = async () => {
        if (!venueId || !showId || !commissionGroupId) {
            notifyInfo(t('invoices.selectScopeMessage'));
            return;
        }
        if (!showDay && (!showDateFrom || !showDateTo)) {
            notifyError(new Error('Pick a single day, or fill in both From and To dates.'));
            return;
        }
        if (showDateFrom && showDateTo && new Date(showDateFrom) > new Date(showDateTo)) {
            notifyError(new Error('Date From cannot be later than Date To.'));
            return;
        }

        setIsGenerating(true);
        try {
            const result = await generateInvoice(false);
            if (result?.status === 'CONFIRM_REQUIRED') {
                setDuplicateConfirmWarnings(Array.isArray(result?.warnings) ? result.warnings : []);
                setDuplicateConfirmOpen(true);
                return;
            }
            await handleDispatchSuccess(result);
        } catch (error) {
            notifyError(error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleConfirmDuplicateGenerate = async () => {
        setDuplicateConfirmOpen(false);
        setIsGenerating(true);
        try {
            const result = await generateInvoice(true);
            await handleDispatchSuccess(result);
        } catch (error) {
            notifyError(error);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <>
            <Dialog
                open
                onClose={closeAction}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiSelect-select, & input': { textAlign: 'left' },
                }}
            >
                <DialogTitle>{t('invoices.generateTitle')}</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            // min(220px, 100%) so the column min never exceeds the (padded)
                            // dialog width on narrow screens - otherwise the 220px track
                            // overflows the content padding and inputs touch the edges.
                            gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
                            pt: 1,
                        }}
                    >
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={t('reports.filters.venue')}
                            value={venueId || ''}
                            onChange={(e) => setVenueId(e.target.value)}
                        >
                            {venues.map((venue) => (
                                <MenuItem key={venue.id} value={venue.id}>
                                    {venue.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={t('reports.filters.show')}
                            value={showId}
                            onChange={(e) => setShowId(e.target.value)}
                        >
                            <MenuItem value={0}>{t('invoices.selectShow')}</MenuItem>
                            {shows.map((show) => (
                                <MenuItem key={show.id} value={show.id}>
                                    {show.title}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            disabled={!showId}
                            label={t('reports.filters.commissionGroup')}
                            value={commissionGroupId}
                            onChange={(e) => setCommissionGroupId(e.target.value)}
                        >
                            <MenuItem value={0}>{t('invoices.selectCommissionGroup')}</MenuItem>
                            {commissionGroups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            type="date"
                            label={t('invoices.day')}
                            value={showDay}
                            onChange={(e) => pickShowDay(e.target.value)}
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            type="date"
                            label={t('reports.filters.dateFrom')}
                            value={showDateFrom}
                            onChange={(e) => pickShowDateFrom(e.target.value)}
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            type="date"
                            label={t('reports.filters.dateTo')}
                            value={showDateTo}
                            onChange={(e) => pickShowDateTo(e.target.value)}
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAction} variant="outlined">
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerateInvoice}
                        disabled={
                            !venueId ||
                            !showId ||
                            !commissionGroupId ||
                            (!showDay && (!showDateFrom || !showDateTo)) ||
                            isGenerating
                        }
                    >
                        {isGenerating ? t('common.loading') : t('invoices.generateButton')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={duplicateConfirmOpen} onClose={() => setDuplicateConfirmOpen(false)}>
                <DialogTitle>{t('reports.affiliateInvoiceDispatchConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('reports.affiliateInvoiceDispatchConfirmMessage')}
                    </DialogContentText>
                    {duplicateConfirmWarnings.length > 0 && (
                        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            {duplicateConfirmWarnings.map((warning, index) => (
                                <Typography
                                    component="li"
                                    key={index}
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {warning}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDuplicateConfirmOpen(false)} variant="outlined">
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleConfirmDuplicateGenerate} variant="contained">
                        {t('common.yes')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default GenerateInvoiceModal;
